//import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Button from "../../Common/components/Buttons/Button/Button";
import Input from "../../Common/components/Inputs/Input/Input";
import FileImportDialog from "./FileImportDialog";
import "./Sizing.scss";
import TopSection from "./TopSection";

import MsxIcons from "../../Common/components/Icons/MsxIcons/MsxIcons";
import { STEPS } from "../../Common/store/data/commonData";
import { updateConfiguration } from "../../Common/store/slices/common";
import { createDiagram, getPureMotionDesignProfile } from "../../Common/utils/api/api";
import {
  selectRetrofitConfiguration,
  selectSizingDynamics,
  setCurrentSizingStep,
  setSizingDynamics
} from "../store/slices/retrofit";

const NAME = "name";
const motionProfileOptions = ["Positioning Diagram", "CAM Diagram", "Simple motion sequence"];

const LEFT1_NAME = "left1";
const LEFT2_NAME = "left2";
const LEFT3_NAME = "left3";
const LEFT4_NAME = "left4";

const LEFT1_LABEL = "positionValues";
const LEFT2_LABEL = "velocityValues";
const LEFT3_LABEL = "accelerationValues";
const LEFT4_LABEL = "jerkValues";

const LEFT1_COLOR = "#000";
const LEFT2_COLOR = "#5abee7";
const LEFT3_COLOR = "#2da854";
const LEFT4_COLOR = "#bf3b6b";

const YAXIS_COLOR = "#000000";
const YAXIS_ORIENTATION = "left";
const LINE_TYPE = "monotone";
// const DOMAIN = [0, 1];
// const TICKS = [0, 0.2, 0.4, 0.6, 0.8, 1];

const motionProfileMapping = {
  // eslint-disable-next-line prettier/prettier
  Linear: "STRAIGHT",
  "Quadratic parabola": "QUADPARABEL",
  "Polynomial of the 5th degree": "POLY5",
  "Simple sine": "SIMPLESIN",
  "Modified sinoidal": "MODISIN",
  "Modified acceleration trapezoid": "MODACCTRAPEZ",
  "General polynomial of the 5th degree": "GENERALPOLY5"
};

const ChartSection = ({ diagramData, activeCharts }) => {
  const formatValue = (value, name) => {
    const formattedValue = Number(value).toFixed(2);
    return parseFloat(formattedValue).toString();
  };

  // Calculate the maximum absolute value for each chart
  const positionMax = Math.max(...diagramData.map((d) => Math.abs(d.positionValues)));
  const velocityMax = Math.max(...diagramData.map((d) => Math.abs(d.velocityValues)));

  return (
    <div className="mt-5 mr-4">
      <ResponsiveContainer width="100%" height={380}>
        <LineChart data={diagramData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="xValues" fontSize={12} tickFormatter={formatValue} />

          {/* Multiple Y Axes */}

          <YAxis
            yAxisId={LEFT1_NAME}
            tick={<CustomYAxisTick color={LEFT1_COLOR} />}
            orientation={YAXIS_ORIENTATION}
            stroke={YAXIS_COLOR}
            allowDecimals={true}
            domain={[-positionMax, positionMax]}
            // ticks={TICKS}
            interval={0}
          />
          <YAxis
            yAxisId={LEFT2_NAME}
            tick={<CustomYAxisTick color={LEFT2_COLOR} />}
            orientation={YAXIS_ORIENTATION}
            stroke={YAXIS_COLOR}
            allowDecimals={true}
            domain={[-velocityMax, velocityMax]}
            // ticks={TICKS}
            x={40}
          />
          <YAxis
            yAxisId={LEFT3_NAME}
            tick={<CustomYAxisTick color={LEFT3_COLOR} />}
            orientation={YAXIS_ORIENTATION}
            stroke={YAXIS_COLOR}
            allowDecimals={true}
            x={80}
            // domain={[-accelerationMax, accelerationMax]}
            // ticks={TICKS}
          />
          <YAxis
            yAxisId={LEFT4_NAME}
            tick={<CustomYAxisTick color={LEFT4_COLOR} />}
            orientation={YAXIS_ORIENTATION}
            stroke={YAXIS_COLOR}
            allowDecimals={true}
            x={120}
            // domain={[-jerkMax, jerkMax]}
            // ticks={TICKS}
          />

          <Tooltip formatter={formatValue} />
          <Legend />

          {/* Lines corresponding to each Y Axis */}
          <Line
            yAxisId={LEFT1_NAME}
            dot={false}
            type={LINE_TYPE}
            dataKey={LEFT1_LABEL}
            stroke={LEFT1_COLOR}
            strokeWidth={1.8}
            animationDuration={500}
            hide={!activeCharts.position}
          />
          <Line
            yAxisId={LEFT2_NAME}
            dot={false}
            type={LINE_TYPE}
            dataKey={LEFT2_LABEL}
            stroke={LEFT2_COLOR}
            strokeWidth={1.8}
            animationDuration={500}
            hide={!activeCharts.velocity}
          />
          <Line
            yAxisId={LEFT3_NAME}
            dot={false}
            type={LINE_TYPE}
            dataKey={LEFT3_LABEL}
            stroke={LEFT3_COLOR}
            strokeWidth={1.8}
            animationDuration={500}
            hide={!activeCharts.acceleration}
          />
          <Line
            yAxisId={LEFT4_NAME}
            dot={false}
            type={LINE_TYPE}
            dataKey={LEFT4_LABEL}
            stroke={LEFT4_COLOR}
            strokeWidth={1.8}
            animationDuration={500}
            hide={!activeCharts.jerk}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const MotionProfileSection = ({ dynamics, handleInputChange }) => {
  return (
    <div className="flex space-x-3">
      <div className="flex text-sm mt-1">Selected Motion profile</div>
      <select
        className="w-[30%] mb-1 text-sm py-1 px-2 cursor-pointer text-gray-600 border-gray-300"
        name="motionProfile"
        id="motionProfile"
        value={dynamics.motionProfile || ""} // Bind to dynamics.motionProfile
        onChange={handleInputChange} // Update state on change
      >
        {motionProfileOptions.map((option) => {
          return (
            <option key={option} value={option}>
              {option}
            </option>
          );
        })}
      </select>
    </div>
  );
};

const CustomYAxisTick = ({ x, y, payload, color }) => {
  return (
    <text x={x} y={y} fill={color} fontSize={12} textAnchor="end" dy={4}>
      {payload.value}
    </text>
  );
};

const ChartControlButtonsSection = ({ activeCharts, setActiveCharts }) => {
  const [showExport, setShowExport] = useState(false);
  const [showImportedFiles, setShowImportedFiles] = useState(false);
  const exportDropDownRef = useRef(null);
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([
    { id: 1, name: "File name 1", selected: false },
    { id: 2, name: "File name 1", selected: false },
    { id: 3, name: "File name 1", selected: false }
  ]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Handle the file upload
      console.log("Selected file:", file);
      setFiles([...files, { id: files.length + 1, name: file.name, selected: false }]);
    }
  };

  const handleClickOutside = (event) => {
    if (exportDropDownRef.current && !exportDropDownRef.current.contains(event.target)) {
      setShowExport(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const ControlButton = ({ name, icon, active, color, onClick }) => {
    return (
      <Button
        className={`flex justify-center items-center border border-[#626469] w-7 h-6 ${
          active ? "bg-primary text-white" : ""
        }`}
        onClick={onClick}
      >
        <MsxIcons name={icon} size="nano" />
        <span className="text-[13px]">{name}</span>
      </Button>
    );
  };

  return (
    <div className="flex justify-between">
      <div className="flex space-x-2 mt-2">
        <Button className="flex justify-center items-center border border-[#626469] w-6 h-6">
          <MsxIcons name="search" size="nano" />
        </Button>
        <Button className="flex justify-center items-center border border-[#626469] w-6 h-6">
          <MsxIcons name="download" size="nano" />
        </Button>
        <Button className="flex justify-center items-center border border-[#626469] w-6 h-6">
          <MsxIcons name="arrow_down" size="nano" />
        </Button>
        <Button className="flex justify-center items-center border border-[#626469] w-6 h-6">
          <MsxIcons name="arrow_up" size="nano" />
        </Button>
        <ControlButton
          name="P"
          icon="arrow4_up"
          active={activeCharts.position}
          color="#000"
          onClick={() => setActiveCharts({ ...activeCharts, position: !activeCharts.position })}
        />
        <ControlButton
          name="V"
          icon="arrow4_up"
          active={activeCharts.velocity}
          color="#5abee7"
          onClick={() => setActiveCharts({ ...activeCharts, velocity: !activeCharts.velocity })}
        />
        <ControlButton
          name="A"
          icon="arrow4_up"
          active={activeCharts.acceleration}
          color="#DC0A0A"
          onClick={() => setActiveCharts({ ...activeCharts, acceleration: !activeCharts.acceleration })}
        />
        <ControlButton
          name="J"
          icon="arrow4_up"
          active={activeCharts.jerk}
          color="#BF3B6B"
          onClick={() => setActiveCharts({ ...activeCharts, jerk: !activeCharts.jerk })}
        />
      </div>
      <div>
        <div className="flex space-x-2 mt-2 mr-5">
          <div class="relative">
            <div className="flex justify-center items-center border border-[#626469] h-6 rounded-sm">
              <Button onClick={handleImportClick} className="flex justify-center items-center space-x-1 ml-2 mr-[5px]">
                <MsxIcons name="download" size="nano" />
                <span className="text-[13px]">Import</span>
              </Button>
              <div className="h-full border-l-[1px] border-[#626469] ml-[1px] mr-[1px]" />
              <Button
                onClick={() => setShowImportedFiles(!showImportedFiles)}
                className="flex justify-center items-center ml-1 mt-[1px] mr-1"
              >
                <MsxIcons name="arrow_down" size="nano" />
              </Button>
              <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
            </div>
          </div>
          <div class="relative">
            <Button
              onClick={() => setShowExport(!showExport)}
              className="flex justify-center items-center border border-[#626469] h-6 rounded-sm"
            >
              <div className="flex m-2 justify-center items-center space-x-1">
                <MsxIcons name="upload" size="nano" />
                <span className="text-[13px]">Export</span>
                <span className="flex justify-center items-center bg-black text-white rounded-full w-3 h-3 ml-2 mr-2 text-[10px]">
                  2
                </span>
                <MsxIcons name="arrow_down" size="nano" className="mt-[1px]" />
              </div>
            </Button>
            {showExport && (
              <ul ref={exportDropDownRef} className="absolute z-[1000] w-full bg-white border rounded-sm">
                <li>
                  <div className="flex items-center justify-center w-full bg-white space-x-2 h-6  hover:bg-zinc-200/60 focus:bg-zinc-200/60 border-b-[1px]">
                    <MsxIcons name="quotation" size="nano" className="text-[#0075A3] w-3 h-3" />
                    <span className="text-[13px] text-[#0075A3]">Excel</span>
                  </div>
                </li>
                <li>
                  <div className="flex items-center justify-center w-full bg-white space-x-2 h-6  hover:bg-zinc-200/60 focus:bg-zinc-200/60">
                    <MsxIcons name="file_placeholder" size="nano" className="text-[#0075A3] w-3 h-3" />
                    <span className="text-[13px] text-[#0075A3]">PDF</span>
                  </div>
                </li>
              </ul>
            )}
          </div>
          {showImportedFiles && (
            <FileImportDialog
              files={files}
              setFiles={setFiles}
              showImportedFiles={showImportedFiles}
              handleImportClick={handleImportClick}
              setShowImportedFiles={setShowImportedFiles}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const LeftSection = ({ diagramData, dynamics, handleInputChange }) => {
  const [activeCharts, setActiveCharts] = useState({
    position: true,
    velocity: true,
    acceleration: true,
    jerk: true
  });

  return (
    <div className="w-[70%] flex-col">
      <div className="mb-4">Add description here to guide the user better throughout the process for each tab.</div>
      <div className="pl-3">
        <MotionProfileSection dynamics={dynamics} handleInputChange={handleInputChange} />
        <ChartControlButtonsSection activeCharts={activeCharts} setActiveCharts={setActiveCharts} />
        <ChartSection diagramData={diagramData} activeCharts={activeCharts} />
      </div>
    </div>
  );
};

const AddInputSelect = ({
  name,
  label,
  subLabel,
  value = "",
  handleInputChange,
  labelRequired = false,
  options = [], // Add options prop for select dropdown
  disabled = false // Add disabled prop
}) => {
  return (
    <>
      <div className="flex flex-col mb-[4px] ml-3">
        <div className="flex items-center mb-1">
          {/* Label and required indicator */}
          <span className="flex-1 md:text-xs">{label}</span>
          {labelRequired && <span className="light-error-active ml-1">*</span>}
        </div>
        <div className="flex items-center space-x-2">
          {/* Render a select dropdown if options are provided */}
          {options.length > 0 && (
            <select
              name={name}
              value={value}
              onChange={handleInputChange}
              className={`w-full rounded-md border-gray-300 text-sm py-1 px-2 ${
                disabled ? "bg-gray-200 cursor-not-allowed text-gray-500" : "bg-white cursor-pointer"
              }`}
              disabled={disabled} // Disable the select if disabled is true
            >
              {options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}
        </div>
        {subLabel && <span className="flex-1 md:text-xs mt-1 text-gray-500">{subLabel}</span>}
      </div>
    </>
  );
};

const AddInput = ({
  name,
  label,
  subLabel,
  value = "",
  handleInputChange,
  labelRequired = false,
  min = null,
  max = null,
  isNumber = false,
  disabled = false // Add disabled prop
}) => {
  return (
    <>
      <div className="flex flex-col mb-[4px] ml-3">
        <div className="flex-row">
          <span className="flex-1 md:text-xs">{label}</span>
          {labelRequired && <span className="light-error-active">*</span>}
        </div>
        <span className="flex-1 md:text-xs mr-5">{subLabel}</span>
        <div className="flex flex-row mr-5">
          <Input
            type={isNumber ? "number" : "text"}
            pattern="^\d+(\.\d{1,2})?$"
            autoComplete="off"
            required
            name={name}
            value={value}
            label=""
            placeHolder="Enter value"
            labelRequired={labelRequired}
            min={min}
            max={max}
            onChange={handleInputChange}
            inputClasses={`w-full rounded-md ${
              disabled ? "bg-gray-200 cursor-not-allowed text-gray-500" : "bg-white cursor-pointer"
            }`}
            inputRounded={true}
            disabled={disabled} // Disable the input if disabled is true
          />
        </div>
      </div>
    </>
  );
};

const ApplySection = ({ enable, handleContinue }) => {
  return (
    <div className="flex-row justify-center mr-4">
      <div className="flex items-center mb-2 ">
        <input
          type="checkbox"
          className={`mr-2 w-5 h-5 appearance-none border-1 rounded-md cursor-pointer transition-all duration-100 ${"bg-white"}`}
          checked={false}
          //onChange={() => toggleSelectFile(file.id)}
        />
        <span className="flex-1 md:text-xs break-words whitespace-normal">
          Save as new template to reuse within my configuration
        </span>
      </div>

      <qds-button
        id="naviguator-button-continue"
        {...(!enable ? { disabled: "" } : {})}
        importance="emphasized"
        onClick={() => {
          handleContinue();
        }}
        text="Go to gearbox selection"
      />
    </div>
  );
};

const RightSection = ({ handleInputChange, handleSegmentInputChange, dynamics, setDiagramData, handleContinue }) => {
  const dispatch = useDispatch();
  const configuration = useSelector(selectRetrofitConfiguration);
  const [hideDetails, setHideDetails] = useState(false);
  const subLabeltext = "A ??? is any component, sensor/actuator, that is connected to the Control ";
  const drawPositionningDiagram = async () => {
    const diagram = await createDiagram(configuration.configId, "PosDiagram");
    console.log("diagram", diagram);
    let pureMotionDesign = await getPureMotionDesignProfile({
      DiagramType: "PosDiagram",
      Name: diagram.name,
      Id: diagram.id,
      segments: dynamics.segments?.map((segment) => {
        return {
          name: segment.name,
          start: {
            time: {
              value: segment.startPoint.startTime,
              unit: "Second",
              dimension: "Time"
            },
            acceleration: {
              value: segment.startPoint.startAcceleration,
              unit: "MetersPerSquareSecond",
              dimension: "Acceleration"
            },
            speed: {
              value: segment.startPoint.startSpeed,
              unit: "MetersPerSecond",
              dimension: "Speed"
            }
          },
          end: {
            acceleration: {
              value: segment.endPoint.endAcceleration,
              unit: "MetersPerSquareSecond",
              dimension: "Acceleration"
            },
            speed: {
              value: segment.endPoint.endSpeed,
              unit: "MetersPerSecond",
              dimension: "Speed"
            },
            time: {
              value: segment.endPoint.endTime,
              unit: "Second",
              dimension: "Time"
            }
          },
          distance: {
            // the distance of each segment is segment+distance + the sum of all previous segments
            value:
              segment.id === 0
                ? segment.motionProfile.distance
                : Number(segment.motionProfile.distance) +
                  dynamics.segments?.slice(0, segment.id).reduce((acc, curr) => acc + Number(curr.distance), 0),
            unit: "Meter",
            dimension: "Length"
          },
          dwellTime: {
            value: segment.motionProfile.dwellTime,
            unit: "Second",
            dimension: "Time"
          },
          velocityMax: {
            value: segment.motionProfile.maximalSpeed,
            unit: "MetersPerSecond",
            dimension: "Speed"
          },
          accelerationMax: {
            value: segment.motionProfile.peakAcceleration,
            unit: "MetersPerSquareSecond",
            dimension: "Acceleration"
          },
          decelerationMax: {
            value: segment.motionProfile.maximalDeceleration,
            unit: "MetersPerSquareSecond",
            dimension: "Acceleration"
          },
          jerkMax: {
            value: segment.motionProfile.maximalJerk,
            unit: "MetersPerCubicSecond",
            dimension: "Jerk"
          }
        };
      })
    });
    pureMotionDesign = pureMotionDesign?.payload;
    console.log("pureMotionDesign", pureMotionDesign);

    const xValues = pureMotionDesign.profile.timeBasedProfile.segments.map((segment) => segment.xValues).flat();
    const positionValues = pureMotionDesign.profile.timeBasedProfile.segments
      .map((segment) => segment.positionValues)
      .flat();
    const velocityValues = pureMotionDesign.profile.timeBasedProfile.segments
      .map((segment) => segment.velocityValues)
      .flat();
    const accelerationValues = pureMotionDesign.profile.timeBasedProfile.segments
      .map((segment) => segment.accelerationValues)
      .flat();
    const jerkValues = pureMotionDesign.profile.timeBasedProfile.segments.map((segment) => segment.jerkValues).flat();

    // build diagramData
    let newDiagramData = [];
    for (let i = 0; i < xValues.length; i++) {
      newDiagramData.push({
        xValues: xValues[i],
        positionValues: positionValues[i],
        velocityValues: velocityValues[i],
        accelerationValues: accelerationValues[i],
        jerkValues: jerkValues[i]
      });
    }

    // console.log("newDiagramData", newDiagramData);
    setDiagramData(newDiagramData);
    dispatch(setSizingDynamics({ ...dynamics, pureMotionDesign }));
  };

  const drawCamDiagram = async () => {
    const diagram = await createDiagram(configuration.configId, "CamDiagram");
    console.log("diagram", diagram);

    let camMotionDesign = await getPureMotionDesignProfile({
      DiagramType: "CamDiagram",
      Name: diagram.name,
      Id: diagram.id,
      segments: dynamics.segments?.map((segment) => {
        console.log("segment", segment);
        console.log("motionProfileMapping", motionProfileMapping[segment.camDiagram.motionProfile]);
        return {
          name: segment.name,
          motionProfile: motionProfileMapping[segment.camDiagram.motionProfile], // Ensure motionProfile is set to the enum value
          start: {
            condition: segment.camDiagram.startCondition,
            x: parseFloat(segment.camDiagramLeftSegmentEdge.x),
            y: parseFloat(segment.camDiagramLeftSegmentEdge.y),
            slope: parseFloat(segment.camDiagramLeftSegmentEdge.slope),
            curvature: parseFloat(segment.camDiagramLeftSegmentEdge.curvature)
          },
          end: {
            condition: segment.camDiagram.endCondition,
            x: parseFloat(segment.camDiagramRightSegmentEdge.x),
            y: parseFloat(segment.camDiagramRightSegmentEdge.y),
            slope: parseFloat(segment.camDiagramRightSegmentEdge.slope),
            curvature: parseFloat(segment.camDiagramRightSegmentEdge.curvature)
          }
        };
      })
    });

    camMotionDesign = camMotionDesign?.payload;
    console.log("camMotionDesign", camMotionDesign);

    const xValues = camMotionDesign.profile.timeBasedProfile.segments.map((segment) => segment.xValues).flat();
    const positionValues = camMotionDesign.profile.timeBasedProfile.segments
      .map((segment) => segment.positionValues)
      .flat();
    const velocityValues = camMotionDesign.profile.timeBasedProfile.segments
      .map((segment) => segment.velocityValues)
      .flat();
    const accelerationValues = camMotionDesign.profile.timeBasedProfile.segments
      .map((segment) => segment.accelerationValues)
      .flat();
    const jerkValues = camMotionDesign.profile.timeBasedProfile.segments.map((segment) => segment.jerkValues).flat();

    // Build diagramData
    let newDiagramData = [];
    for (let i = 0; i < xValues.length; i++) {
      newDiagramData.push({
        xValues: xValues[i],
        positionValues: positionValues[i],
        velocityValues: velocityValues[i],
        accelerationValues: accelerationValues[i],
        jerkValues: jerkValues[i]
      });
    }

    setDiagramData(newDiagramData);
    dispatch(setSizingDynamics({ ...dynamics, camMotionDesign }));
  };

  const handleApply = () => {
    if (dynamics.motionProfile === "Positioning Diagram") {
      drawPositionningDiagram();
    } else if (dynamics.motionProfile === "CAM Diagram") {
      drawCamDiagram();
    }
  };

  return (
    <div className="w-[30%] border-solid border-l-[1px] shadow-md max-h-[80vh]">
      <div className=" flex flex-col h-full">
        <div className="mt-1 mb-1 h-[5%]">
          <TopSection
            hideDetails={hideDetails}
            setHideDetails={setHideDetails}
            mandatoryfieldsMissing={dynamics.name === "" ? 1 : 0}
          />
        </div>
        <div className="flex border-solid border-t-[1px] mb-2" />
        <qds-button
          class="px-10 m-auto"
          size="small"
          importance="emphasized"
          onClick={handleApply}
          text="Apply"
        ></qds-button>
        <div className="ml-3 thin-scrollbar overflow-visible h-full">
          <AddInput
            name={NAME}
            value={dynamics.name}
            label="Name"
            labelRequired={true}
            handleInputChange={handleInputChange}
            subLabel={hideDetails ? subLabeltext : ""}
          />
          <AddInput
            name="description"
            value={dynamics.description}
            label="Description"
            handleInputChange={handleInputChange}
            subLabel={hideDetails ? subLabeltext : ""}
          />
          <AddInput
            name="secondsCycle"
            value={dynamics.secondsCycle}
            label="Seconds/cycle"
            handleInputChange={handleInputChange}
            subLabel={hideDetails ? subLabeltext : ""}
            isNumber={true}
          />
          <AddInput
            name="cycleMinutes"
            value={dynamics.cycleMinutes}
            label="Cycle/minutes"
            handleInputChange={handleInputChange}
            subLabel={hideDetails ? subLabeltext : ""}
            isNumber={true}
          />
          <AddInput
            name="xOffset"
            value={dynamics.xOffset}
            label="xOffset"
            handleInputChange={handleInputChange}
            isNumber={true}
          />
          <AddInput
            name="numberOfSegments"
            value={dynamics.numberOfSegments}
            label="Number of segments"
            min={1}
            max={100}
            handleInputChange={handleInputChange}
            subLabel={hideDetails ? subLabeltext : ""}
            isNumber={true}
          />
          <div className="mb-3" />
          {/* Segments */}
          {/* depending on dynamics.numberOfSegments display a div for each */}
          <div className="border-t">
            {dynamics.numberOfSegments <= 100 &&
              dynamics.segments?.map((segment) => {
                return (
                  <Accordion key={segment.id}>
                    <AccordionSummary
                      //expandIcon={<ExpandMoreIcon />}
                      expandIcon={<p></p>}
                      aria-controls={"panel" + segment.id + "-content"}
                      id={"panel" + segment.id + "-header"}
                      className="md:text-xs"
                    >
                      {segment.name}
                    </AccordionSummary>
                    {dynamics.motionProfile === "Positioning Diagram" && (
                      <AccordionDetails className="md:text-xs divide-y">
                        <div className="pb-3">
                          <p className="mb-3 text-sm font-bold">Motion profile</p>
                          <AddInput
                            name="distance"
                            value={segment.motionProfile.distance}
                            label="Distance"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "motionProfile")}
                            isNumber={true}
                          />
                          <AddInput
                            name="dwellTime"
                            value={segment.motionProfile.dwellTime}
                            label="Dwell time"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "motionProfile")}
                            isNumber={true}
                          />
                          <AddInput
                            name="maximalSpeed"
                            value={segment.motionProfile.maximalSpeed}
                            label="Maximal speed"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "motionProfile")}
                            isNumber={true}
                          />
                          <AddInput
                            name="peakAcceleration"
                            value={segment.motionProfile.peakAcceleration}
                            label="Peak acceleration"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "motionProfile")}
                            isNumber={true}
                          />
                          <AddInput
                            name="maximalDeceleration"
                            value={segment.motionProfile.maximalDeceleration}
                            label="Maximal deceleration"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "motionProfile")}
                            isNumber={true}
                          />
                          <AddInput
                            name="maximalJerk"
                            value={segment.motionProfile.maximalJerk}
                            label="Maximal jerk"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "motionProfile")}
                            isNumber={true}
                          />
                        </div>
                        <div className="py-3">
                          <p className="mb-3 text-sm font-bold">Start point</p>
                          <AddInput
                            name="startSpeed"
                            value={segment.startPoint.startSpeed}
                            label="Start speed"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "startPoint")}
                            isNumber={true}
                          />
                          <AddInput
                            name="startAcceleration"
                            value={segment.startPoint.startAcceleration}
                            label="Start acceleration"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "startPoint")}
                            isNumber={true}
                          />
                          <AddInput
                            name="startTime"
                            value={segment.startPoint.startTime}
                            label="Start time"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "startPoint")}
                            isNumber={true}
                          />
                        </div>
                        <div className="py-3">
                          <p className="mb-3 text-sm font-bold">End point</p>
                          <AddInput
                            name="endSpeed"
                            value={segment.endPoint.endSpeed}
                            label="End speed"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "endPoint")}
                            isNumber={true}
                          />
                          <AddInput
                            name="endAcceleration"
                            value={segment.endPoint.endAcceleration}
                            label="End acceleration"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "endPoint")}
                            isNumber={true}
                          />
                          <AddInput
                            name="endTime"
                            value={segment.endPoint.endTime}
                            label="End time"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "endPoint")}
                            isNumber={true}
                          />
                        </div>
                      </AccordionDetails>
                    )}
                    {dynamics.motionProfile === "CAM Diagram" && (
                      <AccordionDetails className="md:text-xs divide-y">
                        <div className="pb-3">
                          <p className="mb-3 font-bold text-sm">CAM Diagram</p>
                          <AddInputSelect
                            name="startCondition"
                            value={segment.camDiagram.startCondition}
                            label="Start condition"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "camDiagram")}
                            options={["Dwell", "Motion", "Reversal", "Velocity"]}
                          />
                          <AddInputSelect
                            name="endCondition"
                            value={segment.camDiagram.endCondition}
                            label="End condition"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "camDiagram")}
                            options={["Dwell", "Motion", "Reversal", "Velocity"]}
                          />
                          <AddInputSelect
                            name="motionProfile"
                            value={segment.camDiagram.motionProfile}
                            label="Motion Profile"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "camDiagram")}
                            options={[
                              "Linear",
                              "Quadratic parabola",
                              "Polynomial of the 5th degree",
                              "Simple sine",
                              "Modified sinoidal",
                              "Modified acceleration trapezoid",
                              "General polynomial of the 5th degree"
                            ]}
                          />
                          <AddInputSelect
                            name="splineInterpolation"
                            value={segment.camDiagram.splineInterpolation}
                            label="Spline interpolation"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "camDiagram")}
                            options={["--"]}
                            disabled={true}
                          />
                          <AddInputSelect
                            name="inputParameters"
                            value={segment.camDiagram.inputParameters}
                            label="Input parameters"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "camDiagram")}
                            options={["--"]}
                            disabled={true}
                          />
                        </div>
                        <div className="py-3">
                          <p className="mb-3 text-sm font-bold">Left segment edge - start</p>
                          <AddInput
                            name="x"
                            value={segment.camDiagramLeftSegmentEdge.x}
                            label="X"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramLeftSegmentEdge")
                            }
                            isNumber={true}
                          />
                          <AddInput
                            name="y"
                            value={segment.camDiagramLeftSegmentEdge.y}
                            label="Y"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramLeftSegmentEdge")
                            }
                            isNumber={true}
                          />
                          <AddInput
                            name="slope"
                            value={segment.camDiagramLeftSegmentEdge.slope}
                            label="Slope"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramLeftSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="curvature"
                            value={segment.camDiagramLeftSegmentEdge.curvature}
                            label="Curvature"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramLeftSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                        </div>
                        <div className="py-3">
                          <p className="mb-3 text-sm font-bold">Right segment edge - end</p>
                          <AddInput
                            name="x"
                            value={segment.camDiagramRightSegmentEdge.x}
                            label="X"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramRightSegmentEdge")
                            }
                            isNumber={true}
                          />
                          <AddInput
                            name="y"
                            value={segment.camDiagramRightSegmentEdge.y}
                            label="Y"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramRightSegmentEdge")
                            }
                            isNumber={true}
                          />
                          <AddInput
                            name="slope"
                            value={segment.camDiagramRightSegmentEdge.slope}
                            label="Slope"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramRightSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="curvature"
                            value={segment.camDiagramRightSegmentEdge.curvature}
                            label="Curvature"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "camDiagramRightSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                        </div>
                      </AccordionDetails>
                    )}
                    {dynamics.motionProfile === "Simple motion sequence" && (
                      <AccordionDetails className="md:text-xs divide-y">
                        <div className="pb-3">
                          <p className="mb-3 text-sm font-bold">Simple motion sequence</p>
                          <AddInputSelect
                            name="inputParameters"
                            value={segment.simpleMotionSequence.inputParameters}
                            label="Input Parameters"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "simpleMotionSequence")}
                            options={[
                              "time end, position end",
                              "position end, total time",
                              "time end, distance",
                              "distance, total time",
                              "time end, velocity end",
                              "velocity end, total time"
                            ]}
                          />
                          <AddInput
                            name="smooth"
                            value={segment.simpleMotionSequence.smooth}
                            label="Smooth"
                            handleInputChange={(e) => handleSegmentInputChange(e, segment.id, "simpleMotionSequence")}
                            isNumber={true}
                          />
                        </div>
                        <div className="py-3">
                          <p className="mb-3 text-sm font-bold">Left segment edge - start</p>
                          <AddInput
                            name="time"
                            value={segment.simpleMotionSequenceLeftSegmentEdge.time}
                            label="Time"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceLeftSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="position"
                            value={segment.simpleMotionSequenceLeftSegmentEdge.position}
                            label="Position"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceLeftSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="velocity"
                            value={segment.simpleMotionSequenceLeftSegmentEdge.velocity}
                            label="Velocity"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceLeftSegmentEdge")
                            }
                            isNumber={true}
                          />
                        </div>
                        <div className="py-3">
                          <p className="mb-3 text-sm font-bold">Right segment edge - end</p>
                          <AddInput
                            name="time"
                            value={segment.simpleMotionSequenceRightSegmentEdge.time}
                            label="Time"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceRightSegmentEdge")
                            }
                            isNumber={true}
                          />
                          <AddInput
                            name="totalTime"
                            value={segment.simpleMotionSequenceRightSegmentEdge.totalTime}
                            label="Total time"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceRightSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="position"
                            value={segment.simpleMotionSequenceRightSegmentEdge.position}
                            label="Position"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceRightSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="distance"
                            value={segment.simpleMotionSequenceRightSegmentEdge.distance}
                            label="Distance"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceRightSegmentEdge")
                            }
                            isNumber={true}
                            disabled={true}
                          />
                          <AddInput
                            name="velocity"
                            value={segment.simpleMotionSequenceRightSegmentEdge.velocity}
                            label="Velocity"
                            handleInputChange={(e) =>
                              handleSegmentInputChange(e, segment.id, "simpleMotionSequenceRightSegmentEdge")
                            }
                            isNumber={true}
                          />
                        </div>
                      </AccordionDetails>
                    )}
                  </Accordion>
                );
              })}
          </div>
          <div></div>
          {/* Segments */}
        </div>
        <div class="flex p-2 justify-center absolute bottom-0 bg-white border-t right-[18px] w-[30%] z-10">
          <ApplySection enable={dynamics.isValid} handleContinue={handleContinue} />
        </div>
        <div className="mb-10" />
      </div>
    </div>
  );
};

const createSegment = (id) => ({
  id,
  name: `Segment ${id + 1}`,
  motionProfile: {
    distance: "300",
    dwellTime: "0",
    maximalSpeed: "1000",
    peakAcceleration: "10000",
    maximalDeceleration: "10000",
    maximalJerk: "100000"
  },
  startPoint: {
    startSpeed: "0",
    startAcceleration: "0",
    startTime: "0"
  },
  endPoint: {
    endSpeed: "0",
    endAcceleration: "0",
    endTime: "0.5"
  },
  camDiagram: {
    startCondition: "Dwell", // Default value for start condition
    endCondition: "Motion", // Default value for end condition
    motionProfile: "Linear", // Default motion profile
    splineInterpolation: "", // Empty by default
    inputParameters: "" // Empty by default
  },
  camDiagramLeftSegmentEdge: {
    x: "0",
    y: "0",
    slope: "0",
    curvature: "0"
  },
  camDiagramRightSegmentEdge: {
    x: "100",
    y: "0",
    slope: "0",
    curvature: "0"
  },
  simpleMotionSequence: {
    inputParameters: "time end, velocity end",
    smooth: "1"
  },
  simpleMotionSequenceLeftSegmentEdge: {
    time: "0",
    position: "0",
    velocity: "0"
  },
  simpleMotionSequenceRightSegmentEdge: {
    time: "1",
    totalTime: "1",
    position: "0",
    distance: "0",
    velocity: "0"
  }
});

const SizingDefineDynamics = () => {
  const dynamics = useSelector(selectSizingDynamics);
  const dispatch = useDispatch();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const newData = {
      ...dynamics,
      [name]: value
    };

    // Reset segments when the motion profile changes
    if (name === "motionProfile") {
      newData.segments = []; // Clear the segments
      newData.numberOfSegments = 0; // Reset the number of segments
    }

    // Update segments if the number of segments changes
    if (name === "numberOfSegments" && value !== dynamics.numberOfSegments) {
      if (value < dynamics.segments.length) {
        // Remove extra segments
        newData.segments = dynamics.segments.slice(0, value);
      } else {
        // Add new segments
        const updatedSegments = [...dynamics.segments];
        for (let i = dynamics.segments.length; i < value; i++) {
          updatedSegments.push(createSegment(i));
        }
        newData.segments = updatedSegments;
      }
    }

    dispatch(setSizingDynamics(newData));
  };

  useEffect(() => {
    const isValid = dynamics.name === "" ? false : true;
    dispatch(setSizingDynamics({ ...dynamics, isValid }));
  }, [dynamics.name]);

  const handleSegmentInputChange = (e, segmentId, section) => {
    const { name, value } = e.target;

    const updatedSegments = dynamics.segments.map((segment) => {
      if (segment.id === segmentId) {
        return {
          ...segment,
          [section]: {
            ...segment[section],
            [name]: value
          }
        };
      }
      return segment;
    });

    dispatch(setSizingDynamics({ ...dynamics, segments: updatedSegments }));
  };

  const handleContinue = () => {
    dispatch(setCurrentSizingStep(STEPS.RETROFIT_SIZING.GEARBOX_SELECTION));
    dispatch(updateConfiguration());
  };

  const [diagramData, setDiagramData] = useState([
    // { xValues: 0, positionValues: 0.0, velocityValues: 0.0, accelerationValues: 0.0, jerkValues: 0.1 },
    // { xValues: 20, positionValues: 0.2, velocityValues: 0.4, accelerationValues: 0.6, jerkValues: 0.3 },
    // { xValues: 40, positionValues: 0.4, velocityValues: 0.6, accelerationValues: 0.8, jerkValues: 0.5 },
    // { xValues: 60, positionValues: 0.6, velocityValues: 0.8, accelerationValues: 1.0, jerkValues: 0.7 },
    // { xValues: 80, positionValues: 0.5, velocityValues: 1.0, accelerationValues: 0.5, jerkValues: 0.8 },
    // { xValues: 100, positionValues: 1.0, velocityValues: 0.7, accelerationValues: 0.1, jerkValues: 0.9 }
  ]);

  return (
    <div>
      <div className="flex">
        <LeftSection diagramData={diagramData} dynamics={dynamics} handleInputChange={handleInputChange} />
        <RightSection
          handleInputChange={handleInputChange}
          handleSegmentInputChange={handleSegmentInputChange}
          dynamics={dynamics}
          setDiagramData={setDiagramData}
          handleContinue={handleContinue}
        />
      </div>
    </div>
  );
};

export default SizingDefineDynamics;
